import React from "react";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import JarvisButton from "components/JarvisButton";

import { FINAL_CTA_LABEL, LOGO_SRC } from "../welcomeScreenConstants";
import { resolveSlideLayout } from "../welcomeSlideLayout";
import FeatureBar from "./FeatureBar";
import FeatureGrid from "./FeatureGrid";
import ProductGrid from "./ProductGrid";

const renderFeatureSlot = (slide, classes) => {
  if (slide.featureBarImage) {
    return (
      <Box
        component="img"
        src={slide.featureBarImage}
        alt=""
        className={classes.featureBarImage}
        draggable={false}
      />
    );
  }

  if (slide.features?.length && slide.featuresLayout === "productGrid") {
    return <ProductGrid products={slide.features} classes={classes} />;
  }

  if (slide.features?.length && slide.featuresLayout === "grid") {
    return <FeatureGrid features={slide.features} classes={classes} />;
  }

  if (slide.features?.length) {
    return <FeatureBar features={slide.features} classes={classes} />;
  }

  return null;
};

const renderCta = (classes, onComplete) => (
  <Box className={classes.ctaWrap}>
    <JarvisButton disableEndIcon onClick={onComplete}>
      {FINAL_CTA_LABEL}
    </JarvisButton>
  </Box>
);

const WelcomeSlideContent = ({ slide, classes, onComplete }) => {
  const layout = resolveSlideLayout(slide);
  const showSubtitleInTextBlock = slide.subtitlePlacement !== "belowHero";
  const showHeroTagline =
    slide.subtitlePlacement === "belowHero" && slide.subtitle;
  const showCtaAfterHero =
    slide.showCta && slide.ctaPlacement === "afterHero";
  const showCtaAfterFeatures =
    slide.showCta && slide.ctaPlacement === "afterFeatures";

  return (
    <Box className={classes.slideContent}>
      <Box className={classes.header}>
        <Box
          component="img"
          src={LOGO_SRC}
          alt="Jarvis"
          className={classes.logo}
          draggable={false}
        />
      </Box>

      <Stack className={classes.textBlock} spacing={0}>
        <Text component="h1" className={classes.title}>
          {slide.titleParts.map((part, index) => (
            <Box
              key={`${slide.id}-title-${index}`}
              component="span"
              sx={{
                color: part.color,
                display: part.display === "block" ? "block" : "inline",
              }}
            >
              {part.text}
            </Box>
          ))}
        </Text>
        {showSubtitleInTextBlock ? (
          <Text component="p" className={classes.subtitle}>
            {slide.subtitle}
          </Text>
        ) : null}
      </Stack>

      <Box
        className={classes.heroWrap}
        sx={{
          flex: layout.heroFlexGrow ? "1 1 0" : "0 0 auto",
          minHeight: layout.heroMinHeight,
          ...(layout.heroMaxHeight ? { maxHeight: layout.heroMaxHeight } : {}),
        }}
      >
        <Box
          component="img"
          src={slide.heroImage}
          alt=""
          className={classes.heroImage}
          draggable={false}
        />
      </Box>

      {showHeroTagline ? (
        <Box className={classes.heroTaglineWrap}>
          <Text component="p" className={classes.heroTagline}>
            {slide.subtitle}
          </Text>
        </Box>
      ) : null}
      {showCtaAfterHero ? (
        <Box className={classes.ctaSlot}>{renderCta(classes, onComplete)}</Box>
      ) : null}

      <Box
        className={classes.featureBarWrap}
        sx={layout.heroFlexGrow ? { marginTop: "auto" } : undefined}
      >
        {renderFeatureSlot(slide, classes)}
      </Box>

      {showCtaAfterFeatures ? (
        <Box className={classes.ctaSlot}>{renderCta(classes, onComplete)}</Box>
      ) : null}
    </Box>
  );
};

export default WelcomeSlideContent;
